import React, { ReactElement, Fragment, useState, useEffect } from 'react';
import { styled } from '@material-ui/styles';
import { useDispatch } from 'react-redux';
import { canGenerousUserMakeOffer, renderAvatar, tsSince, toaster } from '../../common';
import OfferAccept from '../../components/pages/Offers/OfferAccept';
import OfferAcceptUnlock from '../../components/pages/Offers/OfferAcceptUnlock';
import OfferAcceptMessage from '../../components/pages/Offers/OfferAcceptMessage';
import OfferEdit from '../../components/pages/Offers/OfferEdit';
import useModal from '../../hooks/useModal';
import Button from '../buttons/Button';
import FavoriteOfferButton from '../buttons/FavoriteOfferButton';
import OfferAmountButton from '../buttons/OfferAmountButton';
import WYPButtonIcon from '../icons/WYPButtonIcon';
import { getTextColor, type } from '../../style';
import {
  declineOffer,
  sendCancel,
  profileAcceptOffer,
  profileCounterOffer,
  counterModalClose,
} from '../../actions/profileTrackAction';
import { ignoreOffer, setShowOfferSuccess, showCreateNewOfferModal } from '../../actions/offerActions';
import { AccountMaker } from '../../models/Account';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { refreshExternalProfile } from '../../actions/profileActions';
import useMandatoryWall from '../../hooks/useMandatoryWall';
import { useCommonModal } from 'contexts/ModalContext';

interface PendingOfferPanelProps {
  offer: any;
  createdAt: any;
  myProfile: any;
  otherProfile: any;
  otherAccount: any;
  isGenerous: any;
  acceptOffer: any;
  acceptOfferBtn: any;
  counterOffer: any;
}

const Time = styled('div')(() => ({
  ...getTextColor().LIGHT_GRAY,
  ...type.XS,
  letterSpacing: -0.3,
  textAlign: 'center',
  marginTop: 10,
}));

const PendingAmountContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'start',
  justifyContent: 'center',
});

const ButtonContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});

const CancelButton = styled(Button)({});

const CustomButtonStyles = {
  borderRadius: '4px !important',
  border: '1px solid #E21D4A !important',
  padding: '11px 24px',
  lineHeight: '16px',
  letterSpacing: -0.32,
  '&:hover': {
    opacity: 0.7,
    color: '#fff',
    backgroundColor: '#2490de',
  },
};

const CustomAmountButtonStyle = {
  marginTop: 0,
  marginBottom: 0,
};

const PendingOfferPanel = ({
  offer,
  createdAt,
  myProfile,
  otherProfile,
  otherAccount,
  acceptOffer,
  acceptOfferBtn,
  isGenerous,
  counterOffer,
}: PendingOfferPanelProps): ReactElement => {
  const dispatch = useDispatch();
  const [rawAmount, setRawAmount] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [isAcceptingOffer, setIsAcceptingOffer] = useState(false);
  const [showOfferEditModal, setShowOfferEditModal] = useState(false);
  const [showOfferAcceptModal, setShowOfferAcceptModal] = useState(false);
  const [showOfferAcceptUnlockModal, setShowOfferAcceptUnlockModal] = useState({
    isOpen: false,
    amount: '',
    creditCost: null,
  });
  const [showOfferAcceptMessageModal, setShowOfferAcceptMessageModal] = useState({ isOpen: false, amount: '' });
  const { confirmDeclineOffer, confirmModal, resetModal } = useModal();
  const iAmSender = offer.hash_id === myProfile.hash_id;
  const accountModel = AccountMaker.create(myProfile);
  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();
  const { showMandatoryWall } = useMandatoryWall();

  useEffect(() => {
    setOfferPrice(
      formatPrice(
        {
          initial: offer ? offer.currency_original.data.initial : '',
          label: offer ? offer.currency_original.data.label_unicode : '',
        },
        offer.price_original
      )
    );

    setRawAmount(offer.price_original);
  }, [offer]);

  const handleIgnoreOfferAction = (hashId: string) => {
    dispatch(ignoreOffer(hashId, false, false, null, null));
    setTimeout(() => dispatch(refreshExternalProfile(hashId)), 0);
  };

  const handleCancelSentOffer = () => {
    confirmModal({
      dataTestID: 'offer-cancel-sent-offers',
      modalContentLabel: 'Offers Cancel Sent Offers',
      confirmationProps: {
        title: `Cancel ${isGenerous ? 'Offer' : 'Request'}?`,
        message: `Are you sure you want to cancel your ${
          isGenerous ? 'offer' : 'request'
        } to <strong>${otherAccount.username}</strong>?`,
        confirmText: `Cancel ${isGenerous ? 'Offer' : 'Request'}`,
        confirmAction: () => {
          handleIgnoreOfferAction(otherAccount.hash_id);
          sendCancel();

          mixpanelTrackEvent({
            eventName: EVENT_NAMES.OFFER_CANCELLED,
            payload: {
              eventProperties: {
                Amount: offer.price_original,
                User: myProfile.hash_id,
                Source: 'profile',
              },
            },
            otherAccountHashId: offer.initiator_hash_id,
          });

          resetModal();
        },
      },
    });
  };

  const handleNewOffer = (hashId: string) => {
    dispatch(ignoreOffer(hashId, false, false, null, null));
    sendCancel();
    mixpanelTrackEvent({
      eventName: EVENT_NAMES.OFFER_CANCELLED,
      payload: {
        eventProperties: {
          Amount: offer.price_original,
          User: myProfile.hash_id,
          Source: 'profile',
        },
      },
      otherAccountHashId: offer.initiator_hash_id,
    });

    setShowOfferEditModal(false);
    setTimeout(() => {
      dispatch(refreshExternalProfile(hashId));
    }, 0);

    dispatch(showCreateNewOfferModal(true));
  };

  const handleEditSentOffer = () => {
    if (!iAmSender && myProfile.can_make_offer) {
      return;
    }

    showMandatoryWall(
      () => {
        if (canGenerousUserMakeOffer(myProfile)) {
          if (accountModel.shouldShowTooManyLockedOffersDeals()) {
            setIsAcceptingOffer(false);
            setShowTooManyPendingOfferModal(true);
            return;
          } else if (accountModel.shouldShowTooManySentOffers()) {
            setIsAcceptingOffer(false);
            setShowTooManyPendingOfferModal(true);
            setModalArgs({
              type: 'sent',
            });

            return;
          }
        }

        setShowOfferEditModal(true);
      },
      () => undefined,
      'Edit Sent Offer'
    );
  };

  const handleIgnoreOffer = async () => {
    confirmDeclineOffer({
      isGenerous,
      username: otherAccount.username,
      onConfirm: () => {
        handleIgnoreOfferAction(otherAccount.hash_id);
        declineOffer();

        mixpanelTrackEvent({
          eventName: EVENT_NAMES.OFFER_DECLINED,
          payload: {
            eventProperties: {
              Amount: offer.price_original,
              User: myProfile.hash_id,
              Source: 'Profile',
            },
          },
          otherAccountHashId: offer.initiator_hash_id,
        });

        toaster.warn('Offer Declined');
        resetModal();
      },
    });
  };

  const handleCounterOffer = formData => {
    profileCounterOffer();
    return counterOffer(formData);
  };

  const handleForceOpenCounterModal = () => {
    showMandatoryWall(
      () => {
        if (canGenerousUserMakeOffer(myProfile)) {
          if (accountModel.shouldShowTooManyLockedOffersDeals('counter')) {
            setIsAcceptingOffer(false);
            setShowTooManyPendingOfferModal(true);
            return;
          } else if (accountModel.shouldShowTooManySentOffers()) {
            setIsAcceptingOffer(false);
            setShowTooManyPendingOfferModal(true);
            setModalArgs({ type: 'sent' });
            return;
          }
        }

        setShowOfferAcceptModal(true);
      },
      () => undefined,
      'Counter Offer'
    );
  };

  const handleAcceptUnlock = (amount = '', creditCost) => {
    setShowOfferAcceptUnlockModal({
      isOpen: true,
      amount,
      creditCost: creditCost,
    });
  };

  const handleAttractiveMessage = (amount = '') => {
    setIsAcceptingOffer(false);
    showMandatoryWall(() => {
      setShowOfferAcceptMessageModal({ isOpen: true, amount });
    });
  };

  const handleOfferSuccess = (
    toggle: boolean,
    amount = '',
    isCounter = false,
    isRevised = false,
    isSuggest = false
  ) => {
    setOfferPrice(amount);
    setShowOfferEditModal(false);
    setShowOfferAcceptModal(false);
    dispatch(
      setShowOfferSuccess({
        isOpen: toggle,
        amount,
        isCounter,
        isRevised,
        isSuggest,
      })
    );
  };

  const formatPrice = (currency, price) => {
    if (!currency || !price) {
      return '';
    }

    return `${currency.initial}${String.fromCharCode(currency.label)}${price}`;
  };

  const handleGenerousAcceptOffer = () => {
    if (canGenerousUserMakeOffer(myProfile)) {
      if (accountModel.shouldShowTooManyLockedOffersDeals('accept')) {
        setIsAcceptingOffer(false);
        setShowTooManyPendingOfferModal(true);
        return;
      }
    }

    showMandatoryWall(
      () => {
        profileAcceptOffer();
        acceptOffer(otherAccount.hash_id, '');
      },
      () => undefined,
      'Accept Offer'
    );
  };

  const handleAcceptOffer = () => {
    setIsAcceptingOffer(true);

    isGenerous ? handleGenerousAcceptOffer() : handleAttractiveMessage(offerPrice);
  };

  const renderOfferAcceptButton = () => {
    return (
      <Button
        id={'btn-accept-offer'}
        buttonRef={acceptOfferBtn}
        buttonType="error"
        onClick={handleAcceptOffer}
        type="button"
        disabled={isAcceptingOffer}
        customStyle={CustomButtonStyles}
      >
        <WYPButtonIcon style={{ marginRight: 6 }} />
        {isAcceptingOffer ? 'Accepting Offer' : 'Accept Offer'}
      </Button>
    );
  };

  const handleCloseOfferAcceptUnlock = () => {
    setShowOfferAcceptUnlockModal({
      isOpen: false,
      amount: '',
      creditCost: null,
    });
  };

  return offer ? (
    <Fragment>
      <div>
        <PendingAmountContainer>
          {!iAmSender && <FavoriteOfferButton type={'decline'} onClick={handleIgnoreOffer} style={{ margin: 0 }} />}
          <OfferAmountButton
            type={'receive'}
            price={offerPrice}
            viewType={'grid'}
            hasHoverEffect={iAmSender ? true : false}
            onClick={handleEditSentOffer}
            customBoxStyle={CustomAmountButtonStyle}
          />
          {!iAmSender && (
            <FavoriteOfferButton
              type={'counter'}
              onClick={handleForceOpenCounterModal}
              style={{ margin: 0, borderWidth: 1 }}
            />
          )}
        </PendingAmountContainer>
        {createdAt ? <Time>{createdAt && tsSince(createdAt)}</Time> : null}
      </div>
      <ButtonContainer>
        {iAmSender ? (
          <CancelButton
            buttonType={'secondaryRed'}
            onClick={handleCancelSentOffer}
            data-test-id="offer-cancel-action"
            customStyle={CustomButtonStyles}
          >
            Cancel {iAmSender && offer.suggest ? 'Request' : 'Offer'}
          </CancelButton>
        ) : (
          renderOfferAcceptButton()
        )}
      </ButtonContainer>
      <OfferEdit
        isModalOpen={showOfferEditModal}
        avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
        username={otherAccount.username}
        hashId={otherAccount.hash_id}
        closeModal={() => setShowOfferEditModal(false)}
        currencyInitial={otherAccount.currency.data.initial}
        currencyLabel={otherAccount.currency.data.label_unicode}
        currentUserIsGenerous={isGenerous}
        buttonID="editOffer"
        currentOffer={rawAmount}
        successOpen={handleOfferSuccess}
        sex={otherAccount && otherAccount.profile.data.sex}
        onCancelAndSendNewOfferHandler={() => {
          handleNewOffer(otherAccount.hash_id);
        }}
      />

      <OfferAccept
        isModalOpen={showOfferAcceptModal}
        acceptUnlock={handleAcceptUnlock}
        avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
        username={otherAccount.username}
        hashId={otherAccount.hash_id}
        onHandleSubmit={() => {
          acceptOffer(otherAccount.hash_id, '');
        }}
        successOpen={handleOfferSuccess}
        counterOffer={handleCounterOffer}
        closeModal={() => {
          setShowOfferAcceptModal(false);
          counterModalClose();
        }}
        currentUserIsGenerous={isGenerous}
        buttonID="acceptOffer"
        offer={{ data: offer }}
        ignoreOffer={handleIgnoreOffer}
        forceCounter={true}
        onHandleAttractiveSubmit={handleAttractiveMessage}
        pageTitle={'Profile'}
      />

      <OfferAcceptUnlock
        isModalOpen={showOfferAcceptUnlockModal.isOpen}
        offerAmount={showOfferAcceptUnlockModal.amount}
        avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
        username={otherAccount.username}
        successOpen={handleCloseOfferAcceptUnlock}
        buttonID="interestedOffers"
        currentUserIsGenerous={isGenerous}
        creditCost={showOfferAcceptUnlockModal.creditCost}
        currentCredit={myProfile.credits}
        hashId={otherAccount.hash_id}
        closeModal={handleCloseOfferAcceptUnlock}
      />

      <OfferAcceptMessage
        isModalOpen={showOfferAcceptMessageModal.isOpen}
        offerAmount={showOfferAcceptMessageModal.amount}
        avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
        username={otherAccount.username}
        buttonID="interestedOffers"
        closeModal={() => setShowOfferAcceptMessageModal({ isOpen: false, amount: '' })}
        onSubmit={dealMessage => {
          profileAcceptOffer();
          return acceptOffer(otherAccount.hash_id, dealMessage);
        }}
      />
    </Fragment>
  ) : (
    <div />
  );
};

export default PendingOfferPanel;
